export interface DocsPageSummary {
  slug: string;
  title: string;
  summary: string;
}

export interface DocsSection {
  id: string;
  title: string;
  pages: DocsPageSummary[];
}

export interface DocsManifest {
  sections: DocsSection[];
  latestRelease?: string;
  lastSeenRelease?: string;
  unseenReleases: number;
}

export interface DocsHeading {
  depth: number;
  text: string;
  anchor: string;
}

export interface DocsPage {
  slug: string;
  title: string;
  summary: string;
  sectionId: string;
  sectionTitle: string;
  markdown: string;
  headings: DocsHeading[];
  /** Each ```diagram block, rendered by the server, in document order. */
  diagrams: DocsDiagram[];
}

/** What causes a transition. Colours its arrow and number, and groups it in the list under the diagram. */
export type DocsDiagramKind = "success" | "failure" | "automatic" | "action" | "external" | "neutral";

export interface DocsDiagramTransition {
  /** Printed on the arrow; the arrow and its badge carry `data-edge` with this number. */
  number: number;
  label: string;
  description?: string;
  from: string;
  to: string;
}

export interface DocsDiagramGroup {
  kind: DocsDiagramKind;
  title: string;
  transitions: DocsDiagramTransition[];
}

export interface DocsDiagram {
  /** Laid out on the server; every label is XML-escaped there. */
  svg: string;
  groups: DocsDiagramGroup[];
}

export interface DocsSearchEntry {
  slug: string;
  anchor?: string;
  title: string;
  sectionTitle: string;
  summary?: string;
  headings: DocsHeading[];
  text: string;
}

export type ChangelogComponent = "server" | "portal" | "client" | "updater";
export type ChangelogImpact = "action-required" | "heads-up" | "no-action";
export type ChangelogCategory = "install" | "db-sync" | "updater" | "config" | "portal" | "fixes";

export interface ChangelogEntry {
  anchor: string;
  title: string;
  components: ChangelogComponent[];
  impact: ChangelogImpact;
  category: ChangelogCategory;
  markdown: string;
}

export interface ChangelogSection {
  category: ChangelogCategory;
  title: string;
  entries: ChangelogEntry[];
}

export interface ChangelogBuild {
  version: string;
  tag: string;
}

export interface ChangelogRelease {
  id: string;
  date: string;
  summary: string;
  server: boolean;
  client?: ChangelogBuild;
  updater?: ChangelogBuild;
  serverPullRequests: number[];
  clientPullRequests: number[];
  sections: ChangelogSection[];
  alsoShipped?: string;
}

export interface Changelog {
  releases: ChangelogRelease[];
  unreleased: ChangelogEntry[];
  lastSeenRelease?: string;
}

export const ChangelogComponentLabels: Record<ChangelogComponent, string> = {
  server: "Server",
  portal: "Portal",
  client: "Client",
  updater: "Updater",
};

export const ChangelogImpactLabels: Record<ChangelogImpact, string> = {
  "action-required": "Action required",
  "heads-up": "Heads-up",
  "no-action": "No action needed",
};

export const ChangelogImpactDescriptions: Record<ChangelogImpact, string> = {
  "action-required": "You need to do something: install, set a value, or stop following a procedure.",
  "heads-up": "Behaviour you will notice has changed.",
  "no-action": "Transparent to you, but worth knowing.",
};

export const ChangelogCategoryOrder: ChangelogCategory[] = ["install", "db-sync", "updater", "config", "portal", "fixes"];

export const ChangelogCategoryTitles: Record<ChangelogCategory, string> = {
  install: "Install requirement changes",
  "db-sync": "DB / sync logic changes",
  updater: "Updater changes",
  config: "Config / external settings changes",
  portal: "Portal changes",
  fixes: "Fixes",
};

/** The element id of a release on the changelog page. Mirrors `ChangelogParser.ReleaseAnchor` on the server. */
export const releaseAnchor = (releaseId: string) => `release-${releaseId.replace(/\./g, "-")}`;

/**
 * Orders release ids (`yyyy-MM-dd`, or `yyyy-MM-dd.N` for the Nth promotion of a day) by date, then sequence.
 * A plain string comparison would put `.10` before `.2`. Missing ids sort first.
 */
export const compareReleaseIds = (left?: string, right?: string): number => {
  const parse = (id?: string): [string, number] | null => {
    if (!id) return null;
    const [date, sequence] = id.split(".");
    return [date, sequence ? Number(sequence) : 1];
  };
  const a = parse(left);
  const b = parse(right);
  if (!a || !b) return (a ? 1 : 0) - (b ? 1 : 0);
  if (a[0] !== b[0]) return a[0] < b[0] ? -1 : 1;
  return a[1] - b[1];
};
